import { Specialty } from './../interfaces/session.interface';
import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  firstValueFrom,
  fromEvent,
  Observable,
  Subject,
  takeUntil,
} from 'rxjs';
import {
  abortError,
  FileUploadService,
  isAbortError,
} from './file-upload.service';
import {
  SegmentStart,
  segmentStartsInJoinedChunks,
} from '../utils/recording-container';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { AiScribeService, SubmittedReportPick } from './aiScribe.service';
import { WebSocketService } from './websocket.service';
import { AiScribeStatus } from '../interfaces/aiScribeJob.interface';

interface UploadState {
  sessionId: string;
  uploadId: string;
  key: string;
  currentPartNumber: number;
  uploadedParts: { PartNumber: number; ETag: string }[];
  elapsedTime?: number;
  lastRecordingStartAt?: number;
  /**
   * The draft's generation-template choices.
   */
  generationMode?: string | null;
  generationTemplate?: string | null;
  /**
   * Chunk indexes at which a NEW recorder stream begins.
   */
  segmentStarts?: number[];
  /**
   * Seconds of recording already elapsed when each of `segmentStarts` began,
   * aligned by position.
   */
  segmentStartOffsets?: number[];
}

/**
 * Local bookkeeping for one scribe's recording backup (web AI Scribe), in the
 * `recordingBackups` store. Non-PHI: sizes, indexes, timestamps and an id.
 */
export interface RecordingBackupRecord {
  /** `${sessionId}_${aiScribeId}`, the same key as every other store. */
  sessionId: string;
  /** Client-minted; must not be lost while the draft lives. */
  backupId: string;
  /** The server version this browser's audio matches: set on upload and on pull. */
  backedUpVersion: number | null;
  /** Last chunk index included in backedUpVersion; -1 when none. */
  backedUpChunkIndex: number;
  /** Plaintext bytes in backedUpVersion. */
  backedUpBytes: number;
  backedUpDurationSeconds: number;
  /** Epoch ms of the last acknowledged upload. */
  lastBackupAt: number | null;
  backupState: 'synced' | 'failed' | 'unknown';
}

/** One stored chunk, decrypted, as the backup reads it. */
export interface BackupChunk {
  chunkId: string;
  index: number;
  blob: Blob;
  /** Missing on chunks written before backups existed. */
  plaintextByteLength?: number;
}

@Injectable({ providedIn: 'root' })
export class AudioActivityService {
  private dbPromise: Promise<IDBDatabase>;
  websocketService = inject(WebSocketService);
  private audioActivitySubject = new Subject<void>();
  public audioActivity$ = this.audioActivitySubject.asObservable();
  private isAudioActive = false;

  // Encryption key per session
  private cryptoKey: CryptoKey | null = null;
  private currentKeySessionId: string | null = null;
  private readonly MIN_PART_SIZE = 6 * 1024 * 1024; // 6 MB for s3 upload
  private uploadTimer: any;
  private currentUploadId: string | null = null;
  private currentUploadKey: string | null = null;
  private currentPartNumber: number = 0;
  private uploadedParts: { ETag: string; PartNumber: number }[] = [];
  private currentAiScribeId: string | null = null;
  private currentFileExtension: string | null = null;
  private wrappedKeyStored = false;
  private persistingPromise: Promise<void> | null = null;
  private cryptoKeyExtractable = false;
  private keyReadyPromise: Promise<void> | null = null;
  /**
   * The background part upload in flight, if any. One at a time: two would
   * share a part number, and S3 keeps only the last upload of each.
   */
  private uploadInFlight: Promise<void> | null = null;
  private uploadAbort: AbortController | null = null;
  /** How long Submit waits for that part before aborting it. */
  private readonly SUBMIT_UPLOAD_WAIT_MS = 60_000;
  private readonly recordingTimeSource = new BehaviorSubject<{
    [key: string]: number;
  }>({});
  public recordingTime$ = this.recordingTimeSource.asObservable();

  constructor(
    private readonly fileUploadService: FileUploadService,
    private readonly http: HttpClient,
    private readonly aiScribeService: AiScribeService,
  ) {
    this.dbPromise = this.openDB();
  }

  startAudioActivity() {
    this.isAudioActive = true;
    this.audioActivitySubject.next();
  }

  stopAudioActivity() {
    this.isAudioActive = false;
  }

  isActive(): boolean {
    return this.isAudioActive;
  }

  updateLiveTime(key: string, seconds: number) {
    const current = this.recordingTimeSource.value;
    this.recordingTimeSource.next({ ...current, [key]: seconds });
  }

  clearLiveTime(key: string) {
    const current = this.recordingTimeSource.value;
    this.recordingTimeSource.next({ ...current, [key]: 0 });
  }

  private openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      // v9 adds `recordingBackups` (web recording backup bookkeeping).
      const request = indexedDB.open('session-audio-wal', 9); //getting access to DB

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains('chunks')) {
          const store = db.createObjectStore('chunks', { keyPath: 'chunkId' });
          store.createIndex('sessionId_index', ['sessionId', 'index'], {
            unique: true,
          });
        }

        if (!db.objectStoreNames.contains('keys')) {
          db.createObjectStore('keys', { keyPath: 'sessionId' });
        }
        if (!db.objectStoreNames.contains('uploads')) {
          db.createObjectStore('uploads', { keyPath: 'sessionId' });
        }
        if (!db.objectStoreNames.contains('transcriptions')) {
          db.createObjectStore('transcriptions', {
            keyPath: 'sessionId',
          });
        }
        if (!db.objectStoreNames.contains('templates')) {
          db.createObjectStore('templates', { keyPath: 'sessionId' });
        }
        if (!db.objectStoreNames.contains('recordingBackups')) {
          db.createObjectStore('recordingBackups', { keyPath: 'sessionId' });
        }
      };

      // Another tab still holds an older connection. The upgrade waits for it;
      // that tab's onversionchange (below) closes it, so this resolves on its own.
      request.onblocked = () =>
        console.warn(
          '[IndexedDB] session-audio-wal upgrade is waiting for another tab to close its connection',
        );
      request.onsuccess = () => {
        const db = request.result;
        // A newer tab wants to upgrade: give way rather than blocking it, which
        // would leave that tab unable to save audio at all.
        db.onversionchange = () => {
          db.close();
          console.warn(
            '[IndexedDB] session-audio-wal was upgraded in another tab; reload to keep saving',
          );
        };
        resolve(db);
      };
      // A stale bundle opening a newer database gets a VersionError here.
      request.onerror = () => reject(request.error);
    });
  }

  async isIndexedDBAvailable(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!('indexedDB' in window)) return resolve(false);

      try {
        const request = indexedDB.open('__idb_test__');

        request.onerror = () => resolve(false);
        request.onsuccess = () => {
          request.result.close();
          indexedDB.deleteDatabase('__idb_test__');
          resolve(true);
        };
      } catch {
        resolve(false);
      }
    });
  }

  //Generate a random symmetric key
  // Data → encrypted with random AES key
  // AES key → wrapped by KMS master key (server side)
  async generateSessionKey(
    usersessionId: string,
    aiScribeId: string,
  ): Promise<void> {
    let sessionId = [usersessionId, aiScribeId].join('_');
    if (this.cryptoKey && this.currentKeySessionId === sessionId) return;
    this.cryptoKey = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt'],
    );
    this.cryptoKeyExtractable = true;
    this.wrappedKeyStored = false;
    this.currentKeySessionId = sessionId;
  }

  /**
   * Makes this draft's key the loaded one: restored when this browser has a
   * wrapped copy, otherwise created and persisted. Throws rather than leave the
   * caller with no key, or with another draft's key.
   */
  async ensureSessionKey(
    usersessionId: string,
    aiScribeId: string,
  ): Promise<void> {
    const sessionId = [usersessionId, aiScribeId].join('_');
    if (
      this.cryptoKey &&
      this.currentKeySessionId === sessionId &&
      this.wrappedKeyStored
    ) {
      return;
    }
    if (await this.isKeyPersisted(usersessionId, aiScribeId)) {
      await this.restoreSessionKey(usersessionId, aiScribeId);
    } else {
      await this.generateSessionKey(usersessionId, aiScribeId);
      await this.persistSessionKey(usersessionId, aiScribeId);
    }
    if (
      !this.cryptoKey ||
      this.currentKeySessionId !== sessionId ||
      !this.wrappedKeyStored
    ) {
      throw new Error(`[KeyEnsure] no usable key for ${sessionId}`);
    }
  }

  async isKeyPersisted(
    usersessionId: string,
    aiScribeId: string,
  ): Promise<boolean> {
    let sessionId = [usersessionId, aiScribeId].join('_');
    const db = await this.dbPromise;
    const tx = db.transaction('keys', 'readonly');
    const store = tx.objectStore('keys');
    return new Promise((resolve) => {
      const req = store.get(sessionId);
      req.onsuccess = () => resolve(!!req.result);
      req.onerror = () => resolve(false);
    });
  }

  async hasTranscription(key: string): Promise<boolean> {
    const record = await this.restoreTranscription(key);
    return !!record && record?.trim().length > 0;
  }

  async canResumeSession(
    providerId: string | undefined,
    uuid: string,
  ): Promise<boolean> {
    const key = [providerId, uuid].join('_');

    const [lastChunk, hasText, elapsedTime] = await Promise.all([
      this.getLastChunkIndex(key),
      this.hasTranscription(key),
      this.getElapsedTime(key),
    ]);

    return lastChunk > 0 || hasText || elapsedTime > 0;
  }

  /**
   * Save multipart S3 upload state in IndexedDB
   */
  async storeUploadState(
    sessionId: string,
    overrides?: {
      elapsedTime?: number;
      lastRecordingStartAt?: number | null;
    },
  ) {
    const db = await this.dbPromise;
    const tx = db.transaction('uploads', 'readwrite');
    const store = tx.objectStore('uploads');

    const existing = await new Promise<UploadState | null>((resolve) => {
      const req = store.get(sessionId);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => resolve(null);
    });

    store.put({
      sessionId,
      uploadId: this.currentUploadId,
      key: this.currentUploadKey,
      currentPartNumber: this.currentPartNumber,
      uploadedParts: this.uploadedParts,

      // preserve or override
      elapsedTime: overrides?.elapsedTime ?? existing?.elapsedTime ?? 0,
      lastRecordingStartAt:
        overrides?.lastRecordingStartAt ??
        existing?.lastRecordingStartAt ??
        null,
      // Preserved so this full put() does not drop what
      // saveDraftSelections() saved
      generationMode: existing?.generationMode ?? null,
      generationTemplate: existing?.generationTemplate ?? null,
      segmentStarts: existing?.segmentStarts ?? [],
      segmentStartOffsets: existing?.segmentStartOffsets ?? [],
    });

    return new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async ensureKeyIsReady(): Promise<void> {
    if (this.keyReadyPromise) {
      await this.keyReadyPromise;
    }
  }

  async updateElapsedTime(sessionId: string, elapsedTime: number) {
    const db = await this.dbPromise;
    const tx = db.transaction('uploads', 'readwrite');
    const store = tx.objectStore('uploads');

    const existing = await new Promise<UploadState | null>((resolve) => {
      const req = store.get(sessionId);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => resolve(null);
    });

    store.put({
      ...(existing ?? { sessionId }),
      elapsedTime,
    });

    return new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async getElapsedTime(sessionId: string): Promise<number> {
    const db = await this.dbPromise;
    const tx = db.transaction('uploads', 'readonly');
    const store = tx.objectStore('uploads');

    const state = await new Promise<UploadState | null>((resolve) => {
      const req = store.get(sessionId);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => resolve(null);
    });

    return state?.elapsedTime ?? 0;
  }

  /**
   * Restore multipart S3 upload state from IndexedDB
   */
  /**
   * Save a scribe draft's generation-template choices, leaving the rest of its
   * upload state untouched.
   *
   * Separate from storeUploadState()
   */
  async saveDraftSelections(
    sessionId: string,
    selections: {
      generationMode?: string | null;
      generationTemplate?: string | null;
    },
  ): Promise<void> {
    const db = await this.dbPromise;
    const tx = db.transaction('uploads', 'readwrite');
    const store = tx.objectStore('uploads');

    const existing = await new Promise<UploadState | null>((resolve) => {
      const req = store.get(sessionId);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => resolve(null);
    });

    store.put({
      ...(existing ?? { sessionId }),
      sessionId,
      // Should allow caller to remove the selection if needed]
      generationMode:
        'generationMode' in selections
          ? (selections.generationMode ?? null)
          : (existing?.generationMode ?? null),
      generationTemplate:
        'generationTemplate' in selections
          ? (selections.generationTemplate ?? null)
          : (existing?.generationTemplate ?? null),
    });

    return new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /**
   * Records that a new recorder stream starts at this chunk index
   * Called the moment a recorder is started
   */
  async recordSegmentStart(
    sessionId: string,
    chunkIndex: number,
    offsetSeconds: number,
  ): Promise<void> {
    const db = await this.dbPromise;
    const tx = db.transaction('uploads', 'readwrite');
    const store = tx.objectStore('uploads');

    const existing = await new Promise<UploadState | null>((resolve) => {
      const req = store.get(sessionId);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => resolve(null);
    });

    const starts = existing?.segmentStarts ?? [];
    const offsets = existing?.segmentStartOffsets ?? [];
    if (!starts.includes(chunkIndex)) {
      starts.push(chunkIndex);
      offsets.push(offsetSeconds);
    }
    // Sorted as pairs so the two arrays stay aligned by position.
    const paired = starts
      .map((index, i) => ({ index, offset: offsets[i] ?? 0 }))
      .sort((a, b) => a.index - b.index);

    store.put({
      ...(existing ?? { sessionId }),
      sessionId,
      segmentStarts: paired.map((entry) => entry.index),
      segmentStartOffsets: paired.map((entry) => entry.offset),
    });

    return new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /** Chunk indexes at which a new recorder stream begins. */
  async getSegmentStarts(sessionId: string): Promise<number[]> {
    const db = await this.dbPromise;
    const tx = db.transaction('uploads', 'readonly');
    const store = tx.objectStore('uploads');
    const record: UploadState | null = await new Promise((resolve) => {
      const req = store.get(sessionId);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => resolve(null);
    });
    return record?.segmentStarts ?? [];
  }

  /** Where each recorded segment begins, in seconds of combined recording. */
  async getSegmentStartOffsets(sessionId: string): Promise<number[]> {
    const db = await this.dbPromise;
    const tx = db.transaction('uploads', 'readonly');
    const store = tx.objectStore('uploads');
    const record: UploadState | null = await new Promise((resolve) => {
      const req = store.get(sessionId);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => resolve(null);
    });
    return record?.segmentStartOffsets ?? [];
  }

  /**
   * The stored chunk indexes, in the same order getChunks() returns blobs, so
   * the two can be zipped together.
   */
  async getChunkIndexes(sessionId: string): Promise<number[]> {
    const db = await this.dbPromise;
    return new Promise((resolve) => {
      const tx = db.transaction('chunks', 'readonly');
      const index = tx.objectStore('chunks').index('sessionId_index');
      const req = index.getAll(
        IDBKeyRange.bound([sessionId, -Infinity], [sessionId, Infinity]),
      );
      req.onsuccess = () =>
        resolve(
          (req.result || [])
            .sort((a: any, b: any) => a.index - b.index)
            .map((r: any) => r.index),
        );
      req.onerror = () => resolve([]);
    });
  }

  /** The generation-template choices saved with a draft, if any. */
  async getDraftSelections(sessionId: string): Promise<{
    generationMode: string | null;
    generationTemplate: string | null;
  } | null> {
    const db = await this.dbPromise;
    const tx = db.transaction('uploads', 'readonly');
    const store = tx.objectStore('uploads');

    const record: UploadState | null = await new Promise((resolve) => {
      const req = store.get(sessionId);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => resolve(null);
    });
    if (!record) return null;

    return {
      generationMode: record.generationMode ?? null,
      generationTemplate: record.generationTemplate ?? null,
    };
  }

  async restoreUploadState(sessionId: string): Promise<number | null> {
    const db = await this.dbPromise;
    const tx = db.transaction('uploads', 'readonly');
    const store = tx.objectStore('uploads');

    const record: UploadState | null = await new Promise((resolve) => {
      const req = store.get(sessionId);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => resolve(null);
    });

    if (!record) return null;

    // Restore upload state
    this.currentUploadId = record.uploadId;
    this.currentUploadKey = record.key;
    this.currentPartNumber = record.currentPartNumber;
    this.uploadedParts = record.uploadedParts ?? [];

    return record.elapsedTime ?? 0;
  }

  /**
   * Clear stored S3 upload info
   */
  async clearUploadState(sessionId: string) {
    const db = await this.dbPromise;
    const tx = db.transaction('uploads', 'readwrite');
    const store = tx.objectStore('uploads');
    await store.delete(sessionId);

    return new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async persistSessionKey(
    usersessionId: string,
    aiScribeId: string,
  ): Promise<void> {
    let sessionId = [usersessionId, aiScribeId].join('_');
    if (!this.cryptoKey) {
      console.warn('[Service] No cryptoKey found to persist');
      return;
    }
    if (this.wrappedKeyStored && this.currentKeySessionId === sessionId) {
      return;
    }
    if (this.persistingPromise) return this.persistingPromise;

    this.persistingPromise = (async () => {
      try {
        const rawKey = await crypto.subtle.exportKey(
          'raw',
          this.cryptoKey as CryptoKey,
        );

        const response = await firstValueFrom(
          this.http.post<{ wrappedKey: any }>(
            environment.api_host +
              `sessions/${usersessionId}/ai-scribe/session-key/wrap`,
            {
              sessionId,
              rawKey: this.arrayBufferToBase64(rawKey),
            },
          ),
        );

        if (!response?.wrappedKey) {
          throw new Error('Failed to persist session key');
        }

        // Store KMS-wrapped key in IndexedDB
        const db = await this.dbPromise;
        await new Promise<void>((resolve, reject) => {
          const tx = db.transaction('keys', 'readwrite');
          const store = tx.objectStore('keys');

          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(new Error('IDB Transaction Error'));

          store.put({
            sessionId,
            wrappedKey: response.wrappedKey,
          });
        });

        console.log('!!! KEY DEFINITIVELY SAVED TO DISK !!!', sessionId);
        this.wrappedKeyStored = true;
      } catch (e) {
        this.cryptoKey = null;
        this.wrappedKeyStored = false;
        throw e;
      } finally {
        this.persistingPromise = null;
      }
    })();

    return this.persistingPromise;
  }

  // Helper to convert ArrayBuffer → base64
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  async restoreSessionKey(
    usersessionId: string,
    aiScribeId: string,
  ): Promise<boolean> {
    console.log('[KeyRestore] Attempting restore for ID:', usersessionId);
    let sessionId = [usersessionId, aiScribeId].join('_');
    if (this.cryptoKey && this.currentKeySessionId === sessionId) {
      console.log('[KeyRestore] Correct key already in memory.');
      return true;
    }

    // If we have a key but it's for a DIFFERENT session, wipe it first
    if (this.cryptoKey && this.currentKeySessionId !== sessionId) {
      console.warn(
        '[KeyRestore] Key mismatch! Wiping old key to restore correct one.',
      );
      this.destroyKey();
    }
    if (this.keyReadyPromise) {
      await this.keyReadyPromise;
      return this.hasKey();
    }
    this.keyReadyPromise = (async () => {
      try {
        const db = await this.dbPromise;
        const record = await new Promise<any>((resolve) => {
          const tx = db.transaction('keys', 'readonly');
          const req = tx.objectStore('keys').get(sessionId);
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => resolve(null);
        });

        if (!record) throw new Error('No wrapped key found');

        const response = await firstValueFrom(
          this.http.post<{ rawKey: string }>(
            environment.api_host +
              `sessions/${usersessionId}/ai-scribe/session-key/unwrap`,
            { sessionId, wrappedKey: record.wrappedKey },
          ),
        );

        if (!response?.rawKey) throw new Error('Unwrap failed');

        const rawKey = this.base64ToArrayBuffer(response.rawKey);

        this.cryptoKey = await crypto.subtle.importKey(
          'raw',
          rawKey,
          { name: 'AES-GCM' },
          true,
          ['encrypt', 'decrypt'],
        );
        this.currentKeySessionId = sessionId;
        this.wrappedKeyStored = true;
        console.log('Encryption key successfully restored');
      } catch (err) {
        console.error('Failed to restore key:', err);
        this.currentKeySessionId = null;
        this.cryptoKey = null;
        this.keyReadyPromise = null;
        throw err;
      }
    })();

    try {
      await this.keyReadyPromise;
      return true;
    } catch {
      this.keyReadyPromise = null;
      return false;
    }
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const buffer = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      buffer[i] = binary.charCodeAt(i);
    }
    return buffer.buffer;
  }

  hasKey(): boolean {
    return !!this.cryptoKey;
  }

  async encryptBlob(
    blob: Blob,
  ): Promise<{ iv: Uint8Array; data: ArrayBuffer }> {
    if (!this.cryptoKey) throw new Error('Encryption key not initialized');

    const iv = crypto.getRandomValues(new Uint8Array(12));
    const buffer = await blob.arrayBuffer();

    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      this.cryptoKey,
      buffer,
    );

    return { iv, data: encrypted };
  }

  async decryptBlob(
    encrypted: ArrayBuffer,
    iv: Uint8Array,
    mime: string | undefined,
  ): Promise<Blob> {
    if (!this.cryptoKey) throw new Error('Encryption key not initialized');

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      this.cryptoKey,
      encrypted,
    );

    return new Blob([decrypted], { type: mime });
  }

  destroyKey(): void {
    this.cryptoKey = null;
    this.currentKeySessionId = null;
    this.wrappedKeyStored = false;
    this.keyReadyPromise = null;
    console.log('Service key state cleared.');
  }

  /**
   * INDEXED DB SCHEMA STRUCTURE
    {
      chunkId: string;
      sessionId: string;
      index: number;

      encryptedData: ArrayBuffer;
      byteLength: number;
      iv: number[];
      isEncrypted: boolean;

      status: 'PENDING UPLOAD' | 'UPLOADING' | 'UPLOADED' | 'FAILED';
      createdAt: number;
    }
   */

  async addChunk(
    usersessionId: string,
    aiScribeId: string,
    blob: Blob,
    index: number,
  ) {
    let sessionId = [usersessionId, aiScribeId].join('_');
    const db = await this.dbPromise;
    if (!this.wrappedKeyStored) {
      await this.persistSessionKey(usersessionId, aiScribeId);
    }

    // Recording audio is PHI: never written to IndexedDB in plaintext
    if (!this.cryptoKey) {
      throw new Error('[addChunk] no encryption key loaded; chunk not stored');
    }
    const encryptedChunk = await this.encryptBlob(blob);

    const tx = db.transaction('chunks', 'readwrite');
    tx.objectStore('chunks').put({
      chunkId: generateUUID(),
      sessionId,
      index,
      encryptedData: encryptedChunk.data,
      byteLength: encryptedChunk.data.byteLength,
      // byteLength is the ENCRYPTED length; the backup needs the plaintext one.
      plaintextByteLength: blob.size,
      iv: Array.from(encryptedChunk.iv), // store as array
      isEncrypted: true,
      status: 'PENDING UPLOAD',
      createdAt: Date.now(),
    });

    return new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async getChunks(
    sessionId: string | undefined,
    mime: string | undefined,
  ): Promise<Blob[]> {
    const db = await this.dbPromise;

    return new Promise((resolve, reject) => {
      const tx = db.transaction('chunks', 'readonly');
      const store = tx.objectStore('chunks');
      let request: IDBRequest<any[]>;

      if (sessionId) {
        const index = store.index('sessionId_index');

        const range = IDBKeyRange.bound(
          [sessionId, -Infinity],
          [sessionId, Infinity],
        );

        request = index.getAll(range);
      } else {
        request = store.getAll();
      }

      request.onsuccess = async () => {
        const results = request.result || [];

        if (results.length === 0) {
          resolve([]);
          return;
        }

        // Sort by index to ensure correct playback order
        const sortedChunks = results.sort(
          (a: any, b: any) => a.index - b.index,
        );

        const decryptedBlobs: Blob[] = [];
        let hasDecryptionError = false;

        for (const record of sortedChunks) {
          const data = record.encryptedData;
          const iv = new Uint8Array(record.iv || []);

          // Safety checks
          if (!data || !(data instanceof ArrayBuffer || data.byteLength > 0)) {
            console.warn(
              'Skipping invalid chunk: missing or empty data',
              record.chunkId,
            );
            continue;
          }

          try {
            let blob: Blob;
            await this.ensureKeyIsReady();

            if (this.cryptoKey && record.isEncrypted !== false) {
              // default to encrypted if flag missing (backward compat)
              // Attempt decryption
              const decryptedBuffer = await crypto.subtle.decrypt(
                { name: 'AES-GCM', iv },
                this.cryptoKey,
                data,
              );
              blob = new Blob([decryptedBuffer], { type: mime });
            } else {
              // Plaintext fallback (no key or explicitly not encrypted)
              blob = new Blob([data], { type: mime });
            }
            decryptedBlobs.push(blob);
          } catch (err) {
            hasDecryptionError = true;
            console.error(
              `Decryption failed for chunk ${record.chunkId} (index: ${record.index})`,
              err,
            );
          }
        }

        if (hasDecryptionError) {
          console.warn(
            `getChunks completed with ${decryptedBlobs.length}/${sortedChunks.length} successful chunks. Some data may be missing or corrupted.`,
          );
        }
        resolve(decryptedBlobs);
      };

      request.onerror = () => {
        console.error('IndexedDB getAll failed:', request.error);
        reject(request.error);
      };

      // Also reject on transaction error
      tx.onerror = () => {
        console.error('Transaction error during getChunks:', tx.error);
        reject(tx.error);
      };
    });
  }

  // Gte latest index number to continue from index we stopped at - when continuing in progress recording.
  async getLastChunkIndex(sessionId: string): Promise<number> {
    const db = await this.dbPromise;

    return new Promise<number>((resolve) => {
      const tx = db.transaction('chunks', 'readonly');
      const store = tx.objectStore('chunks');
      const index = store.index('sessionId_index');

      const range = IDBKeyRange.bound(
        [sessionId, -Infinity],
        [sessionId, Infinity],
      );

      const request = index.openCursor(range, 'prev'); // highest index first

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          resolve(cursor.value.index + 1);
        } else {
          resolve(0);
        }
      };

      request.onerror = () => resolve(0);
    });
  }

  // chunks that are yet to be uploaded to S3 - batch them up to upload
  async assembleNextPart(
    sessionId: string | undefined,
    { forceFinal = false }: { forceFinal?: boolean } = {},
  ): Promise<{
    chunks: any[];
    totalBytes: number;
    isFinal: boolean;
  }> {
    const db = await this.dbPromise;
    console.log(
      `[assembleNextPart] Assembling next part for sessionId=${sessionId}, forceFinal=${forceFinal}`,
    );

    return new Promise((resolve, reject) => {
      const tx = db.transaction('chunks', 'readonly');
      const idx = tx.objectStore('chunks').index('sessionId_index');
      const range = IDBKeyRange.bound(
        [sessionId, -Infinity],
        [sessionId, Infinity],
      );

      const req = idx.getAll(range);

      req.onsuccess = () => {
        const results = req.result || [];
        // Pending only, the final part included. By the time Submit builds it,
        // the part in flight has settled and stuck UPLOADING chunks have been
        // released (finalizeSession), so an UPLOADING chunk here would be in
        // another part as well.
        const eligible = results
          .filter((c) => c.status === 'PENDING UPLOAD')
          .sort((a, b) => a.index - b.index);

        let totalBytes = 0;
        const selected: any[] = [];

        for (const chunk of eligible) {
          selected.push(chunk);
          totalBytes += chunk.byteLength;

          if (!forceFinal && totalBytes >= this.MIN_PART_SIZE) break;
        }

        if (!forceFinal && totalBytes < this.MIN_PART_SIZE) {
          return resolve({ chunks: [], totalBytes: 0, isFinal: false });
        }

        resolve({
          chunks: selected,
          totalBytes,
          isFinal: forceFinal,
        });
      };

      req.onerror = () => reject(req.error);
    });
  }

  async markUploading(chunkIds: string[]) {
    const db = await this.dbPromise;
    const tx = db.transaction('chunks', 'readwrite');
    const store = tx.objectStore('chunks');

    for (const id of chunkIds) {
      const r = store.get(id);
      r.onsuccess = () => {
        if (r.result) {
          r.result.status = 'UPLOADING';
          store.put(r.result);
        }
      };
    }

    return new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /**
   * Return chunks left UPLOADING by a part that never finished, a reload or a
   * closed tab mid-upload, to pending. Background parts take pending chunks
   * only, so a stuck chunk would otherwise be skipped while later ones upload,
   * and reach S3 out of order in the final part. Safe to re-send: the part it
   * was in was never added to `uploadedParts`, so its number is reused and S3
   * replaces it. Call only when no part is in flight in this tab.
   */
  private async releaseStuckChunks(sessionId: string): Promise<void> {
    const db = await this.dbPromise;
    const tx = db.transaction('chunks', 'readwrite');
    const req = tx
      .objectStore('chunks')
      .index('sessionId_index')
      .openCursor(IDBKeyRange.bound([sessionId, -Infinity], [sessionId, Infinity]));
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) return;
      if (cursor.value.status === 'UPLOADING') {
        cursor.update({ ...cursor.value, status: 'PENDING UPLOAD' });
      }
      cursor.continue();
    };
    return new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  // Mark chunks as uploaded once they are uploaded to s3
  async markChunksUploaded(
    chunkIds: string[],
    partNumber: number,
    etag: string,
  ) {
    const db = await this.dbPromise;
    const tx = db.transaction('chunks', 'readwrite');
    const store = tx.objectStore('chunks');

    for (const id of chunkIds) {
      const req = store.get(id);
      req.onsuccess = () => {
        const record = req.result;
        if (!record) return;

        record.status = 'UPLOADED';
        record.partNumber = partNumber;
        record.etag = etag;

        store.put(record);
      };
    }

    return new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  // Read uploaded parts for CompleteMultipartUpload
  async getUploadedChunks(sessionId: string): Promise<any[]> {
    const db = await this.dbPromise;

    return new Promise((resolve, reject) => {
      const tx = db.transaction('chunks', 'readonly');
      const store = tx.objectStore('chunks');
      const idx = store.index('sessionId_index');

      const range = IDBKeyRange.bound(
        [sessionId, -Infinity],
        [sessionId, Infinity],
      );

      const req = idx.getAll(range);
      req.onsuccess = () => {
        resolve(
          req.result
            .filter((c) => c.status === 'UPLOADED')
            .map((c) => ({ PartNumber: c.partNumber, ETag: c.etag })),
        );
      };
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * Where each take starts in the submitted file. Submit joins every chunk
   * once, in index order, so these are the plaintext chunk sizes summed up to
   * each recorder start.
   */
  async getSubmitSegments(sessionId: string): Promise<SegmentStart[]> {
    const db = await this.dbPromise;
    const records: any[] = await new Promise((resolve, reject) => {
      const req = db
        .transaction('chunks', 'readonly')
        .objectStore('chunks')
        .index('sessionId_index')
        .getAll(IDBKeyRange.bound([sessionId, -Infinity], [sessionId, Infinity]));
      req.onsuccess = () => resolve(req.result ?? []);
      req.onerror = () => reject(req.error);
    });
    const [starts, offsets] = await Promise.all([
      this.getSegmentStarts(sessionId),
      this.getSegmentStartOffsets(sessionId),
    ]);
    return segmentStartsInJoinedChunks(
      records
        .sort((a, b) => a.index - b.index)
        .map((r) => ({ index: r.index, size: plaintextSize(r) })),
      starts,
      offsets,
    );
  }

  getFileExtension(fileName: string): string {
    const extensionMatch = fileName.match(/\.([0-9a-z]+)(?:[\?#]|$)/i);
    // Lowercased and dot-free: this value is part of the S3 key the server
    // derives, and `upload-part-urls` constrains it to ^[a-z0-9]{1,8}$. The
    // regex is case-insensitive, so an uppercase extension would otherwise be
    // carried through verbatim and rejected. Anything that cannot be a valid
    // extension falls back to wav, as it always did; but without the leading
    // dot, which used to produce a doubled '..wav' in the key.
    const extension = (extensionMatch ? extensionMatch[1] : 'wav').toLowerCase();
    return /^[a-z0-9]{1,8}$/.test(extension) ? extension : 'wav';
  }

  async finalizeSession(
    userSessionId: string | undefined,
    healthScribeJobsUUID: string | null,
    totalPcmBytes: number | undefined,
    mime: string | undefined,
    activeUser: string,
    duration: number,
    organizationUser: string,
    organization: string,
    templateIds: string[],
    specialty: string,
    metadata: {
      title?: string;
      languageCode?: string;
      reportPick?: SubmittedReportPick;
    },
  ): Promise<any> {
    if (!userSessionId || !healthScribeJobsUUID)
      throw new Error('Missing sessionId or healthScribeJobsUUID');

    // No new parts, and the one in flight settled: every chunk is then either
    // in a recorded part or pending, so the final part holds each chunk once.
    this.stopBackgroundUpload();
    await this.settleBackgroundUpload(this.SUBMIT_UPLOAD_WAIT_MS);

    try {
      let sessionId = [userSessionId, healthScribeJobsUUID].join('_');
      await this.releaseStuckChunks(sessionId);

      // const job =
      //   await this.docusignService.getJobByUUIDForHealthScribe2(
      //     healthScribeJobsUUID,
      //   );

      // if (job?.recording_path) {
      //   console.log(
      //     '[Service] S3 already complete. Retrying backend processing...',
      //   );
      //   return await this.triggerBackendFinalize(
      //     metadata,
      //     userSessionId,
      //   );
      // }

      await this.restoreUploadState(sessionId);
      if (!this.currentUploadId || !this.currentUploadKey) {
        throw new Error(
          '[Finalize] No upload state found — cannot complete multipart upload',
        );
      }

      // Flush any remaining chunks in IndexedDB
      const { chunks: finalChunks } = await this.assembleNextPart(sessionId, {
        forceFinal: true,
      });
      if (finalChunks.length) {
        //await this.markUploading(finalChunks.map(c => c.chunkId));

        const decryptedBlobs = await Promise.all(
          finalChunks.map(async (c) => {
            const encrypted =
              c.encryptedData instanceof ArrayBuffer
                ? c.encryptedData
                : new Uint8Array(c.encryptedData).buffer;

            if (!c.iv) throw new Error(`Missing IV for chunk ${c.chunkId}`);

            let ivBuffer: Uint8Array;
            if (Array.isArray(c.iv)) {
              ivBuffer = new Uint8Array(c.iv);
            } else if (c.iv instanceof Uint8Array) {
              ivBuffer = c.iv;
            } else {
              throw new Error(`Invalid IV format for chunk ${c.chunkId}`);
            }

            const decryptedBlob = await this.decryptBlob(
              encrypted,
              ivBuffer,
              mime,
            );
            return decryptedBlob;
          }),
        );
        const combined = new Blob(decryptedBlobs, { type: mime });
        console.log(
          `[Finalize] Uploading final part ${this.currentPartNumber}, size=${combined.size}`,
        );

        const etag = await this.uploadPartDirect(
          combined,
          this.currentPartNumber!,
          userSessionId,
        );

        //await this.markChunksUploaded(finalChunks.map(c => c.chunkId), this.currentPartNumber!, etag);
        this.uploadedParts.push({
          PartNumber: this.currentPartNumber!,
          ETag: etag,
        });
        this.currentPartNumber!++;
        await this.storeUploadState(sessionId);
      }

      // Where each take starts. Only a hint for the server, so a failure here
      // never blocks the submit.
      const segments = await this.getSubmitSegments(sessionId).catch((error) => {
        console.warn('[Finalize] Could not work out take boundaries', error);
        return [];
      });

      // Complete multipart upload
      const finalResult = await this.finalizeAndProcess(
        this.currentUploadKey!,
        this.currentUploadId!,
        metadata,
        userSessionId,
        healthScribeJobsUUID,
        mime!,
        activeUser,
        duration,
        organizationUser,
        organization,
        templateIds,
        specialty,
        isTakeHint(segments) ? segments : undefined,
      );
      // const finalResult = await this.fileUploadService
      //   .complete_upload(
      //     this.currentUploadKey!,
      //     this.currentUploadId!,
      //     metadata.title || '',
      //     metadata.languageCode || '',
      //     this.uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber),
      //     userSessionId,
      //   )
      //   .toPromise();

      console.log('[Multipart] Upload completed successfully');
      return finalResult;
    } catch (err) {
      console.error('[Multipart] finalizeSession error:', err);
      throw err;
    } finally {
      this.stopBackgroundUpload();
      this.currentUploadId = null;
      this.currentUploadKey = null;
      this.currentPartNumber = 0;
      this.uploadedParts = [];
      this.currentAiScribeId = null;
      this.currentFileExtension = null;
      this.destroyKey();
    }
  }

  async finalizeAndProcess(
    key: string,
    uploadId: string,
    metadata: any,
    sessionId: string,
    aiScribeId: string,
    mimeType: string,
    activeUser: string,
    duration: number,
    organizationUser: string,
    organization: string,
    templateIds: string[],
    specialty: string,
    segments?: SegmentStart[],
  ) {
    try {
      // STEP 1: Complete the S3 Multipart Upload (The existing call)
      const completeUpload = await firstValueFrom(
        this.fileUploadService.complete_upload(
          key,
          uploadId,
          metadata.title,
          metadata.languageCode,
          this.uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber),
          sessionId,
        ),
      );

      // STEP 2: Create File Asset
      const fileAsset = await firstValueFrom(
        this.aiScribeService.createFileAsset(
          sessionId,
          completeUpload?.bucketName ?? 'lh-document-llm',
          completeUpload.key,
          // From the key itself, so the two always agree: webm, or m4a on
          // Safari, and wav for an upload begun before recordings were named
          // for their real format.
          this.getFileExtension(completeUpload.key),
          activeUser,
          duration,
          segments,
        ),
      );
      // STEP 3 & 4: Link Asset and Create Log
      await firstValueFrom(
        this.aiScribeService.updateAiScribe(sessionId, aiScribeId ?? '', {
          recordingFileId: fileAsset?.id ?? '',
        }),
      );

      if (templateIds.length > 0) {
        this.aiScribeService
          .saveAiScribeTemplates(sessionId, aiScribeId, templateIds)
          .subscribe({
            next: () => {
              console.log('AI Scribe templates saved successfully');
            },
            error: (err) => {
              console.error('Failed to save AI Scribe templates', err);
            },
          });
      }

      await this.aiScribeService
        .createAiScribeLog(sessionId, {
          aiScribeId: aiScribeId ?? '',
          status: AiScribeStatus.NEW,
          eventType: 'HEALTH_NOTE',
          comments: 'Recording submitted',
        })
        .toPromise();

      // STEP 5: Create Content Row
      await firstValueFrom(
        this.aiScribeService.createAiScribeContents(sessionId, aiScribeId),
      );

      // STEP 6: Trigger Processing (RabbitMQ)
      // await firstValueFrom(
      //   this.aiScribeService.triggerSessionProcessing(
      //     this.websocketService.connectionId$() ?? '',
      //     sessionId,
      //     aiScribeId,
      //     [completeUpload.key],
      //     metadata.languageCode,
      //     organizationUser,
      //     organization,
      //     specialty,
      //   ),
      // );

      await firstValueFrom(
        this.aiScribeService.triggerSessionProcessingAiGateway(
          sessionId,
          aiScribeId,
          [completeUpload.key],
          organizationUser,
          organization,
          undefined,
          metadata.reportPick,
        ),
      );

      return { success: true, key: completeUpload.key };
    } catch (e) {
      console.error(
        '[finalizeAndProcess] Failed in submission processing chain:',
        e,
      );
      throw e;
    }
  }

  //Store templates in IndexedDB
  async saveTemplates(
    usersessionId: string,
    aiScribeId: string,
    templateIds: string[],
  ): Promise<void> {
    console.log(
      templateIds,
      'Saving templates for session',
      usersessionId,
      aiScribeId,
    );
    if (!this.dbPromise) return;
    let sessionId = [usersessionId, aiScribeId].join('_');
    const db = await this.dbPromise;
    return new Promise<void>((resolve, reject) => {
      try {
        const tx = db.transaction('templates', 'readwrite');
        const store = tx.objectStore('templates');

        store.put({
          sessionId,
          templateIds,
          updatedAt: Date.now(),
        });

        tx.oncomplete = () => resolve();
        tx.onerror = () => {
          reject(
            new Error(tx.error?.message || 'IndexedDB Put Transaction Failed'),
          );
        };

        tx.onabort = () => {
          reject(new Error('Transaction aborted'));
        };
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    });
  }

  async restoreTemplates(
    usersessionId: string,
    aiScribeId: string,
  ): Promise<string[] | []> {
    if (!this.dbPromise) return [];
    let sessionId = [usersessionId, aiScribeId].join('_');
    const db = await this.dbPromise;
    return new Promise((resolve) => {
      const tx = db.transaction('templates', 'readonly');
      const req = tx.objectStore('templates').get(sessionId);

      req.onsuccess = () => resolve(req.result?.templateIds ?? null);
      req.onerror = () => resolve([]);
    });
  }

  async triggerBackendFinalize(metadata: any, userSessionId: string) {
    return await this.fileUploadService
      .complete_upload(
        this.currentUploadKey!,
        this.currentUploadId!,
        metadata.title || '',
        metadata.languageCode || '',
        this.uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber),
        userSessionId,
      )
      .toPromise();
  }

  /**
   * Helper to reset chunk status to PENDING (used if size < 5MB and forceFinal=false)
   */
  async markChunksPending(chunkIds: string[]) {
    const db = await this.dbPromise;
    const tx = db.transaction('chunks', 'readwrite');
    const store = tx.objectStore('chunks');

    for (const id of chunkIds) {
      const req = store.get(id);
      req.onsuccess = () => {
        const record = req.result;
        if (!record) return;

        record.status = 'PENDING UPLOAD';
        store.put(record);
      };
    }

    return new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /**
   * Presign one part and PUT it straight to S3, returning its ETag.
   *
   * The live paths presign a single part at a time, at the moment the part is
   * ready, rather than batching: a consult has no known part count and can run
   * longer than a presigned URL lives, so a URL issued seconds before use is
   * the only shape that cannot expire mid-recording.
   *
   * On failure the caller leaves the chunks pending and the next tick retries
   * the same part number, which is safe because nothing was written.
   */
  private async uploadPartDirect(
    blob: Blob,
    partNumber: number,
    userSessionId: string,
    signal?: AbortSignal,
  ): Promise<string> {
    if (
      !this.currentAiScribeId ||
      !this.currentFileExtension ||
      !this.currentUploadId
    ) {
      // Reachable only if an upload starts without going through
      // initOrRestoreMultipartUpload. Failing here beats sending a malformed
      // presign request and getting an opaque 400 mid-consult.
      throw new Error('[Multipart] cannot presign — upload context missing');
    }

    const response = await this.untilAborted(
      this.fileUploadService.partUrls(userSessionId, {
        aiScribeId: this.currentAiScribeId,
        uploadId: this.currentUploadId,
        fileExtension: this.currentFileExtension,
        partNumbers: [partNumber],
      }),
      signal,
    );

    const url = response?.urls?.find((u) => u.partNumber === partNumber)?.url;
    if (!url) {
      throw new Error(`No presigned URL returned for part ${partNumber}`);
    }

    const putResponse = await this.untilAborted(
      this.fileUploadService.putPart(url, blob),
      signal,
    );

    const etag = putResponse.headers.get('ETag');
    if (!etag) {
      // The PUT succeeded but the header is unreadable; what a bucket CORS
      // policy missing ExposeHeaders: ["ETag"] looks like from the browser.
      throw new Error(
        `Part ${partNumber} uploaded but no ETag header was readable`,
      );
    }

    // Kept verbatim, quotes included: S3 compares these strings on complete.
    return etag;
  }

  /** First value of `source`, or an AbortError once `signal` aborts. */
  private async untilAborted<T>(
    source: Observable<T>,
    signal?: AbortSignal,
  ): Promise<T> {
    if (!signal) return firstValueFrom(source);
    if (signal.aborted) throw abortError();
    // HttpClient cancels by unsubscribing, which takeUntil does on abort.
    const value = await firstValueFrom(
      source.pipe(takeUntil(fromEvent(signal, 'abort'))),
      { defaultValue: undefined },
    );
    if (signal.aborted || value === undefined) throw abortError();
    return value;
  }

  /**
   * Initialize or restore multipart upload
   */
  async initOrRestoreMultipartUpload(
    file: File,
    sessionId: string | undefined,
    aiScribeId: string,
  ) {
    if (!sessionId || !aiScribeId) {
      throw new Error(
        '[Multipart] Cannot start upload: missing sessionId or aiScribeId',
      );
    }
    const sessionId_aiScribeId = [sessionId, aiScribeId].join('_');

    // Set on both branches. A restored upload still has to presign its next
    // part, and the presign needs these as much as a fresh one does.
    this.currentAiScribeId = aiScribeId;
    this.currentFileExtension = this.getFileExtension(file.name);

    const elapsed = await this.restoreUploadState(sessionId_aiScribeId);

    // A restored upload keeps its own key's extension. The server derives each
    // part's key from it, so an upload begun as .wav must not be asked for
    // .webm parts.
    if (elapsed !== null && this.currentUploadId && this.currentUploadKey) {
      this.currentFileExtension = this.getFileExtension(this.currentUploadKey);
    }

    // A reload mid-part left that part's chunks UPLOADING. Not while this tab
    // has a part in flight: those chunks are its own.
    if (!this.uploadInFlight) {
      await this.releaseStuckChunks(sessionId_aiScribeId);
    }

    // No upload id with state present: audio pulled from a recording backup
    // replaced the chunks an earlier upload referred to (or none existed).
    if (elapsed === null || !this.currentUploadId) {
      const initResponse = await this.fileUploadService
        .init(this.currentFileExtension, aiScribeId, sessionId)
        .toPromise();

      if (!initResponse?.UploadId || !initResponse?.Key) {
        throw new Error('Failed to initialize multipart upload');
      }

      this.currentUploadId = initResponse.UploadId;
      this.currentUploadKey = initResponse.Key;
      this.currentPartNumber = 1;
      this.uploadedParts = [];

      await this.storeUploadState(sessionId_aiScribeId, {
        elapsedTime: elapsed ?? 0,
      });

      console.log('[Multipart] Initialized');
    } else {
      console.log('[Multipart] Restored existing upload');
    }
  }

  async startUploadForIndexedDb(
    sessionId: string | undefined,
    mime: string | undefined,
    userSessionId: string,
  ) {
    if (!sessionId) return;
    console.log(`[BackgroundUpload] Starting for sessionId=${sessionId}`);
    if (this.uploadTimer) {
      clearInterval(this.uploadTimer);
      this.uploadTimer = null;
    }

    this.uploadTimer = setInterval(() => {
      // One part at a time. A slow upload outlasts the 10 s tick, and a second
      // tick would upload under the same part number.
      if (this.uploadInFlight) return;
      const abort = new AbortController();
      this.uploadAbort = abort;
      this.uploadInFlight = this.uploadNextPart(
        sessionId,
        mime,
        userSessionId,
        abort.signal,
      ).finally(() => {
        this.uploadInFlight = null;
        if (this.uploadAbort === abort) this.uploadAbort = null;
      });
    }, 10000); // every 10 seconds
  }

  /**
   * Upload the next 6 MB of pending chunks as the next part. Never rejects:
   * on any failure the chunks go back to pending and the next tick retries
   * them under the same part number.
   */
  private async uploadNextPart(
    sessionId: string,
    mime: string | undefined,
    userSessionId: string,
    signal: AbortSignal,
  ): Promise<void> {
    let chunks: any[] = [];
    try {
      const result = await this.assembleNextPart(sessionId);
      chunks = result.chunks;
      const totalBytes = result.totalBytes;
      const isFinal = result.isFinal;
      console.log(
        `[BackgroundUpload] Chunks ready: ${chunks.length}, totalBytes: ${totalBytes}, isFinal: ${isFinal}`,
      );

      if (chunks.length === 0) return;
      if (totalBytes < this.MIN_PART_SIZE && !isFinal) {
        console.log(
          `[BackgroundUpload] Accumulating data: ${totalBytes} bytes. Waiting for 5MB...`,
        );
        await this.markChunksPending(chunks.map((c) => c.chunkId));
        return;
      }

      // Taken once, at the start: the part number and the upload it belongs to
      // must not change under this part.
      const partNumber = this.currentPartNumber!;
      const uploadId = this.currentUploadId;
      const chunkIds = chunks.map((c) => c.chunkId);

      // Mark chunks as uploading in WAL
      console.log(
        `[BackgroundUpload] Marking chunks as UPLOADING:`,
        chunkIds,
      );
      await this.markUploading(chunkIds);

      // Combine chunk data
      const decryptedBlobs = await Promise.all(
        chunks.map(async (c) => {
          const encrypted =
            c.encryptedData instanceof ArrayBuffer
              ? c.encryptedData
              : new Uint8Array(c.encryptedData).buffer;

          if (!c.iv) throw new Error(`Missing IV for chunk ${c.chunkId}`);

          let ivBuffer: Uint8Array;
          if (Array.isArray(c.iv)) {
            ivBuffer = new Uint8Array(c.iv);
          } else if (c.iv instanceof Uint8Array) {
            ivBuffer = c.iv;
          } else {
            throw new Error(`Invalid IV format for chunk ${c.chunkId}`);
          }

          const decryptedBlob = await this.decryptBlob(
            encrypted,
            ivBuffer,
            mime,
          );
          return decryptedBlob;
        }),
      );
      const combinedBlob = new Blob(decryptedBlobs, { type: mime });

      // Upload part straight to S3 against a URL presigned for this part.
      console.log(
        `[Multipart] Uploading part ${partNumber}, size=${combinedBlob.size}`,
      );
      const etag = await this.uploadPartDirect(
        combinedBlob,
        partNumber,
        userSessionId,
        signal,
      );

      console.log(
        `[BackgroundUpload] Part uploaded successfully, ETag: ${etag}`,
      );

      // A pull replaced the upload while this part was in flight. The part
      // belongs to the old upload, and its chunks are gone.
      if (this.currentUploadId !== uploadId) return;

      // Mark chunks as uploaded
      await this.markChunksUploaded(chunkIds, partNumber, etag);

      // Increment part number for next batch
      this.uploadedParts.push({ PartNumber: partNumber, ETag: etag });
      this.currentPartNumber = partNumber + 1;
      await this.storeUploadState(sessionId);
    } catch (err) {
      if (!isAbortError(err)) console.error('Background upload error:', err);
      if (chunks.length) {
        await this.markChunksPending(chunks.map((c) => c.chunkId)).catch(
          (error) => console.error('Failed to release chunks', error),
        );
      }
    }
  }

  /**
   * Wait for the part in flight, if any, so that no chunk is UPLOADING when
   * Submit builds the final part. Past `capMs` it is aborted: its chunks go
   * back to pending and travel in the final part instead, under the same part
   * number.
   */
  private async settleBackgroundUpload(capMs: number): Promise<void> {
    const inFlight = this.uploadInFlight;
    if (!inFlight) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timedOut = await Promise.race([
      inFlight.then(() => false),
      new Promise<boolean>((resolve) => {
        timer = setTimeout(() => resolve(true), capMs);
      }),
    ]);
    clearTimeout(timer);
    if (timedOut) {
      console.warn('[Finalize] Background part still uploading; aborting it');
      this.uploadAbort?.abort();
      await inFlight;
    }
  }

  async startUploadForMemoryFallback(
    listOfBlobs: Blob[],
    mime: string | undefined,
    usersessionId: string,
  ) {
    console.log(`[BackgroundMemoryUpload] Starting`);
    if (this.uploadTimer) {
      clearInterval(this.uploadTimer);
      this.uploadTimer = null;
    }
    this.uploadTimer = setInterval(async () => {
      try {
        if (listOfBlobs.length === 0) return;
        const totalBytes = listOfBlobs.reduce(
          (sum, blob) => sum + blob.size,
          0,
        );
        if (totalBytes < this.MIN_PART_SIZE) {
          console.log(
            `[Memory Background] Only ${totalBytes} bytes accumulated — waiting for more`,
          );
          return;
        }
        console.log(
          `[Memory Background] Ready to upload ${listOfBlobs.length} blobs (${totalBytes} bytes)`,
        );

        // Combine all current blobs into one part
        const combinedBlob = new Blob(listOfBlobs, { type: mime });

        // Upload part straight to S3 against a URL presigned for this part.
        const etag = await this.uploadPartDirect(
          combinedBlob,
          this.currentPartNumber!,
          usersessionId,
        );

        console.log(
          `[Memory Background] Part uploaded successfully, ETag: ${etag}`,
        );

        listOfBlobs.length = 0;

        // Update multipart tracking
        this.uploadedParts.push({
          PartNumber: this.currentPartNumber!,
          ETag: etag,
        });
        this.currentPartNumber!++;
      } catch (err) {
        console.error('Background memory upload error:', err);
      }
    }, 10000);
  }

  async startBackgroundUpload(
    usersessionId: string | undefined,
    healthScribeJobsUUID: string | null,
    isIdbEnabled: boolean,
    listOfBlobs: Blob[],
    mime: string | undefined,
  ) {
    if (!healthScribeJobsUUID || !usersessionId) {
      console.error(
        '[BackgroundUpload] Missing healthScribeJobsUUID or patientPrimaryProvider.',
      );
      return;
    }
    let sessionId = [usersessionId, healthScribeJobsUUID].join('_');
    if (isIdbEnabled) {
      this.startUploadForIndexedDb(sessionId, mime, usersessionId);
    }
    // in-memory upload fallback
    else {
      this.startUploadForMemoryFallback(listOfBlobs, mime, usersessionId);
    }
  }

  async stopBackgroundUpload() {
    if (this.uploadTimer) clearInterval(this.uploadTimer);
    this.uploadTimer = null;
  }

  async clearSession(
    patientPrimaryProvider?: string,
    healthScribeJobsUUID?: string | null,
  ): Promise<void> {
    let sessionId = [patientPrimaryProvider, healthScribeJobsUUID].join('_');
    const db = await this.dbPromise;
    // Delete chunks
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction('chunks', 'readwrite');
      const store = tx.objectStore('chunks');
      const index = store.index('sessionId_index');
      const range = IDBKeyRange.bound(
        [sessionId, -Infinity],
        [sessionId, Infinity],
      );

      const req = index.openCursor(range);

      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });

    // Delete wrapped key
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction('keys', 'readwrite');
      const store = tx.objectStore('keys');
      const req = store.delete(sessionId);

      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });

    // Delete uploads
    await this.clearUploadState(sessionId);

    // Delete the recording backup bookkeeping with the audio it described.
    await this.deleteBackupRecord(sessionId);

    this.cryptoKey = null;
    this.wrappedKeyStored = false;
  }

  async getBackupRecord(
    sessionId: string,
  ): Promise<RecordingBackupRecord | null> {
    const db = await this.dbPromise;
    return new Promise((resolve) => {
      const req = db
        .transaction('recordingBackups', 'readonly')
        .objectStore('recordingBackups')
        .get(sessionId);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => resolve(null);
    });
  }

  async getAllBackupRecords(): Promise<RecordingBackupRecord[]> {
    const db = await this.dbPromise;
    return new Promise((resolve) => {
      const req = db
        .transaction('recordingBackups', 'readonly')
        .objectStore('recordingBackups')
        .getAll();
      req.onsuccess = () => resolve(req.result ?? []);
      req.onerror = () => resolve([]);
    });
  }

  async putBackupRecord(record: RecordingBackupRecord): Promise<void> {
    const db = await this.dbPromise;
    const tx = db.transaction('recordingBackups', 'readwrite');
    tx.objectStore('recordingBackups').put(record);
    return new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async deleteBackupRecord(sessionId: string): Promise<void> {
    const db = await this.dbPromise;
    const tx = db.transaction('recordingBackups', 'readwrite');
    tx.objectStore('recordingBackups').delete(sessionId);
    return new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /**
   * Every chunk of a scribe, decrypted and in index order, with the fields the
   * backup needs. The bytes are the raw recorder output joined as-is — never
   * getFinalBlob(), whose seekable rewrite would change them.
   */
  async getBackupChunks(
    sessionId: string,
    mime: string | undefined,
  ): Promise<BackupChunk[]> {
    const db = await this.dbPromise;
    const records: any[] = await new Promise((resolve, reject) => {
      const req = db
        .transaction('chunks', 'readonly')
        .objectStore('chunks')
        .index('sessionId_index')
        .getAll(IDBKeyRange.bound([sessionId, -Infinity], [sessionId, Infinity]));
      req.onsuccess = () => resolve(req.result ?? []);
      req.onerror = () => reject(req.error);
    });

    await this.ensureKeyIsReady();
    const chunks: BackupChunk[] = [];
    for (const record of records.sort((a, b) => a.index - b.index)) {
      const data: ArrayBuffer =
        record.encryptedData instanceof ArrayBuffer
          ? record.encryptedData
          : new Uint8Array(record.encryptedData).buffer;
      let plain: ArrayBuffer;
      if (record.isEncrypted !== false) {
        if (!this.cryptoKey) {
          throw new Error('Recording backup: encryption key not ready');
        }
        // A chunk that will not decrypt fails the whole read: a backup with a
        // silent hole in it would be worse than none.
        plain = await crypto.subtle.decrypt(
          { name: 'AES-GCM', iv: new Uint8Array(record.iv || []) },
          this.cryptoKey,
          data,
        );
      } else {
        plain = data;
      }
      chunks.push({
        chunkId: record.chunkId,
        index: record.index,
        blob: new Blob([plain], { type: mime }),
        plaintextByteLength: record.plaintextByteLength,
      });
    }
    return chunks;
  }

  /** Writes plaintext sizes back onto chunks written before they were stored. */
  async setPlaintextByteLengths(
    updates: { chunkId: string; plaintextByteLength: number }[],
  ): Promise<void> {
    if (!updates.length) return;
    const db = await this.dbPromise;
    const tx = db.transaction('chunks', 'readwrite');
    const store = tx.objectStore('chunks');
    for (const update of updates) {
      const req = store.get(update.chunkId);
      req.onsuccess = () => {
        const record = req.result;
        if (record && record.plaintextByteLength === undefined) {
          store.put({ ...record, plaintextByteLength: update.plaintextByteLength });
        }
      };
    }
    return new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /**
   * Replace a scribe's local audio with audio pulled from its recording backup:
   * one chunk per recorder segment, encrypted with this browser's key, the
   * segment starts and timings recorded, the submit upload reset (its parts
   * referred to the chunks being replaced), and the elapsed time set. The
   * local chunks are deleted: nothing is kept under a side key, where no UI
   * could reach it and clearSession would never remove it.
   */
  async replaceChunksWithPulled(
    usersessionId: string,
    aiScribeId: string,
    segments: Blob[],
    segmentStartOffsets: number[],
    durationSeconds: number,
  ): Promise<void> {
    const sessionId = [usersessionId, aiScribeId].join('_');
    // Before anything is deleted: a browser that never recorded this draft has
    // no key for it, and the pulled audio must not be stored in plaintext.
    // If no key can be had, the pull fails and the local audio is untouched.
    await this.ensureSessionKey(usersessionId, aiScribeId);
    const db = await this.dbPromise;

    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction('chunks', 'readwrite');
      const req = tx
        .objectStore('chunks')
        .index('sessionId_index')
        .openCursor(IDBKeyRange.bound([sessionId, -Infinity], [sessionId, Infinity]));
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) return;
        cursor.delete();
        cursor.continue();
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });

    for (let i = 0; i < segments.length; i++) {
      await this.addChunk(usersessionId, aiScribeId, segments[i], i);
    }

    const tx = db.transaction('uploads', 'readwrite');
    const store = tx.objectStore('uploads');
    const req = store.get(sessionId);
    req.onsuccess = () => {
      const existing: UploadState | null = req.result ?? null;
      store.put({
        ...(existing ?? {}),
        sessionId,
        uploadId: null,
        key: null,
        currentPartNumber: 1,
        uploadedParts: [],
        elapsedTime: durationSeconds,
        lastRecordingStartAt: null,
        segmentStarts: segments.map((_, i) => i),
        segmentStartOffsets: segments.map(
          (_, i) => segmentStartOffsets[i] ?? 0,
        ),
      });
    };
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });

    // In-memory submit state follows the stored state.
    this.currentUploadId = null;
    this.currentUploadKey = null;
    this.currentPartNumber = 1;
    this.uploadedParts = [];

    // The sidebar's duration only follows live updates: give it the new length
    this.updateLiveTime(sessionId, durationSeconds);
  }

  //Transcription APIs:
  async saveTranscription(sessionId: string, text: string): Promise<void> {
    if (!this.dbPromise) return;

    const db = await this.dbPromise;
    const tx = db.transaction('transcriptions', 'readwrite');
    tx.objectStore('transcriptions').put({
      sessionId,
      text,
      updatedAt: Date.now(),
    });

    return new Promise((res, rej) => {
      tx.oncomplete = () => res();
      tx.onerror = () => rej(tx.error);
    });
  }

  async restoreTranscription(sessionId: string): Promise<string | null> {
    if (!this.dbPromise) return null;

    const db = await this.dbPromise;
    const tx = db.transaction('transcriptions', 'readonly');
    const req = tx.objectStore('transcriptions').get(sessionId);

    return new Promise((resolve) => {
      req.onsuccess = () => resolve(req.result?.text ?? null);
      req.onerror = () => resolve(null);
    });
  }

  async clearTranscription(sessionId: string): Promise<void> {
    if (!this.dbPromise) return;

    const db = await this.dbPromise;
    const tx = db.transaction('transcriptions', 'readwrite');
    tx.objectStore('transcriptions').delete(sessionId);

    return new Promise((res, rej) => {
      tx.oncomplete = () => res();
      tx.onerror = () => rej(tx.error);
    });
  }
}
function generateUUID(): string {
  if ('randomUUID' in crypto) {
    return crypto.randomUUID();
  } else {
    // fallback to simple RFC4122 version 4 UUID
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = crypto.getRandomValues(new Uint8Array(1))[0] % 16;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}

/**
 * A stored chunk's plaintext size. Chunks written before the size was stored
 * fall back to the ciphertext length less AES-GCM's 16-byte tag, which is the
 * plaintext size exactly.
 */
function plaintextSize(record: {
  plaintextByteLength?: number;
  byteLength: number;
  isEncrypted?: boolean;
}): number {
  if (typeof record.plaintextByteLength === 'number') {
    return record.plaintextByteLength;
  }
  return record.isEncrypted === false ? record.byteLength : record.byteLength - 16;
}

/**
 * Whether take starts are worth sending: more than one, starting at byte 0 and
 * strictly increasing. The server rejects anything else, and the file asset is
 * created after S3 has the recording, so a bad hint must never be sent.
 */
function isTakeHint(segments: SegmentStart[]): boolean {
  return (
    segments.length > 1 &&
    segments[0].byteOffset === 0 &&
    segments.every((s, i) => i === 0 || s.byteOffset > segments[i - 1].byteOffset)
  );
}
